import { AuthService } from "@/online/AuthService";
import type { Platform } from "@/platform/Platform";
import { getSupabaseClient } from "@/online/supabaseClient";
import { callFunction, type CallFunctionOptions } from "@/online/functionsClient";
import { validateDisplayName } from "@/online/validation";
import type { ServiceResult } from "@/online/types";
import { DAILY_SCORING_RULES_VERSION, type DailyChallengeSpecAny } from "./types";
import type { DailyResultSubmission } from "./validation";

export type DailyServiceState = "ok" | "offline" | "error";

export interface DailyChallengeFetchResult {
  state: DailyServiceState;
  /** V1 or V2 - an already-stored date can still be a V1 row. See `DailyChallengeSpecAny`'s doc and the README's "V1 stored rows" section. */
  challenge?: DailyChallengeSpecAny;
  challengeId?: string;
  serverTimeMs?: number;
  nextResetAtMs?: number;
  isQaOverride?: boolean;
  message?: string;
}

export interface DailyAttemptReserveResult {
  state: DailyServiceState | "exhausted";
  challengeId?: string;
  attemptNumber?: number;
  attemptsRemaining?: number;
  message?: string;
}

export interface DailyStatus {
  attemptsReserved: number;
  bestCompleted: boolean;
  bestScore: number | null;
  hasResult: boolean;
}

export interface DailyStatusResult {
  state: DailyServiceState;
  status?: DailyStatus;
  message?: string;
}

export interface DailyResultSubmitResult {
  state: DailyServiceState;
  message?: string;
}

export interface DailyLeaderboardRow {
  rank: number;
  displayName: string;
  completed: boolean;
  score: number;
  activeDurationMs: number;
  steps: number;
  /** Adaptive Daily Points - `null` for a row from a V1 (legacy) challenge_id, which has no scoring profile to have computed it from. See `dailyPoints.ts`. */
  dailyPoints: number | null;
}

export interface DailyLeaderboardResult {
  state: DailyServiceState;
  rows?: DailyLeaderboardRow[];
  message?: string;
}

export interface DailyMyRank {
  rank: number | null;
  completed: boolean;
  score: number;
  activeDurationMs: number;
  steps: number;
  /** Adaptive Daily Points - `null` for a V1 (legacy) challenge_id or when `hasResult` is false. */
  dailyPoints: number | null;
  hasResult: boolean;
}

export interface DailyMyRankResult {
  state: DailyServiceState;
  rank?: DailyMyRank;
  message?: string;
}

interface DailyChallengeFunctionResponse {
  challenge: DailyChallengeSpecAny;
  challengeId: string;
  serverTimeMs: number;
  nextResetAtMs: number;
  isQaOverride: boolean;
}

interface DailyAttemptFunctionResponse {
  challengeId: string;
  attemptNumber: number;
  attemptsRemaining: number;
}

interface DailyStatusRow {
  attempts_reserved: number;
  best_completed: boolean;
  best_score: number | null;
  has_result: boolean;
}

interface DailyLeaderboardRpcRow {
  rank: number;
  display_name: string;
  completed: boolean;
  score: number;
  active_duration_ms: number;
  steps: number;
  daily_points: number | null;
}

interface DailyMyRankRpcRow {
  rank: number | null;
  completed: boolean;
  score: number;
  active_duration_ms: number;
  steps: number;
  daily_points: number | null;
  has_result: boolean;
}

/**
 * The one place Daily Challenge talks to the network - three writes
 * (fetch-or-create the challenge, reserve an attempt, submit a result)
 * go through the Netlify Functions in `netlify/functions/daily-*.ts`
 * (Bearer-authenticated, same pattern as `SupabaseLeaderboardService`);
 * three reads (my status, the leaderboard, my rank) call the public
 * SECURITY DEFINER RPCs directly, same read/write split as the campaign
 * leaderboard. Every public method resolves, never throws - a
 * network/Supabase failure always becomes `{ state: "offline" | "error" }`,
 * never a thrown exception a Scene would have to guard against.
 *
 * Deliberately independent of `OnlineFeaturesStorage`/the campaign
 * ONLINE FEATURES opt-in - Daily Challenge signs in (or reuses an
 * existing anonymous session) via `AuthService.ensureSignedInForDaily()`
 * regardless of that campaign-only toggle, since Daily Challenge is
 * available to every player immediately.
 */
class DailyChallengeClientImpl {
  /**
   * Every public method on this class must resolve, never throw or leave a
   * caller awaiting forever - same guarantee, and same previously-real
   * failure mode, as `ScoreChallengeClient.ensureSignedIn()`: an unexpected
   * rejection from `AuthService.ensureSignedInForDaily()`/
   * `client.auth.getSession()` (a corrupted stored session, a transient
   * Supabase Auth failure) would otherwise become an UNHANDLED PROMISE
   * REJECTION propagating through every caller here (`fetchTodayChallenge()`,
   * `reserveAttempt()`, `submitResult()`, `getMyStatus()`, `getMyRank()`),
   * leaving `DailyChallengeScene`/`DailyResultScene` stuck loading forever
   * instead of falling into the normal offline/error state. Wrapped here
   * once so every caller is protected by construction.
   */
  private async ensureSignedIn(): Promise<{ token: string } | null> {
    try {
      const client = getSupabaseClient();
      if (!client) return null;
      if (!(await AuthService.ensureSignedInForDaily())) return null;
      const { data } = await client.auth.getSession();
      const token = data.session?.access_token;
      return token ? { token } : null;
    } catch {
      return null;
    }
  }

  private classifyError(): "offline" | "error" {
    return typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "error";
  }

  /** Same classification as `classifyError()`, shaped as a full `ServiceResult` (which requires a `message` on its "error" variant) - used only by the two methods below that return `ServiceResult<T>` directly. */
  private classifyErrorResult(): ServiceResult<never> {
    return this.classifyError() === "offline" ? { state: "offline" } : { state: "error", message: "Something went wrong. Please try again." };
  }

  async fetchTodayChallenge(qaDate?: string): Promise<DailyChallengeFetchResult> {
    const auth = await this.ensureSignedIn();
    if (!auth) return { state: this.classifyError() };

    const result = await callFunction<DailyChallengeFunctionResponse>("daily-challenge", auth.token, qaDate ? { qaDate } : {});
    if (!result.ok) {
      return { state: result.status >= 400 && result.status < 500 ? "error" : this.classifyError(), message: result.message };
    }
    return {
      state: "ok",
      challenge: result.data.challenge,
      challengeId: result.data.challengeId,
      serverTimeMs: result.data.serverTimeMs,
      nextResetAtMs: result.data.nextResetAtMs,
      isQaOverride: result.data.isQaOverride,
    };
  }

  async reserveAttempt(): Promise<DailyAttemptReserveResult> {
    const auth = await this.ensureSignedIn();
    if (!auth) return { state: this.classifyError() };

    const result = await callFunction<DailyAttemptFunctionResponse>("daily-attempt", auth.token, {});
    if (!result.ok) {
      if (result.status === 409) return { state: "exhausted", message: result.message };
      return { state: result.status >= 400 && result.status < 500 ? "error" : this.classifyError(), message: result.message };
    }
    return {
      state: "ok",
      challengeId: result.data.challengeId,
      attemptNumber: result.data.attemptNumber,
      attemptsRemaining: result.data.attemptsRemaining,
    };
  }

  /**
   * `options.keepalive` (PR28) is only ever passed by the best-effort
   * teardown DNF report (`GameScene.submitDailyDnfOnTeardownIfNeeded()`) -
   * see `CallFunctionOptions`'s own doc for why a normal (retry-UI-backed)
   * submission must never set it.
   */
  async submitResult(submission: DailyResultSubmission, options?: CallFunctionOptions): Promise<DailyResultSubmitResult> {
    const auth = await this.ensureSignedIn();
    if (!auth) return { state: this.classifyError() };

    const result = await callFunction<{ saved: boolean }>("daily-result", auth.token, submission, options);
    if (!result.ok) {
      return { state: result.status >= 400 && result.status < 500 ? "error" : this.classifyError(), message: result.message };
    }
    return { state: "ok" };
  }

  /**
   * Reuses the exact same `profiles.display_name`/`set_display_name`
   * flow the campaign leaderboard uses (`SupabaseLeaderboardService`) -
   * deliberately NOT going through that service directly, since it gates
   * on `OnlineFeaturesStorage.isEnabled()` (the campaign-only opt-in
   * toggle), which Daily Challenge must never depend on.
   */
  async getMyDisplayName(): Promise<ServiceResult<string | null>> {
    const client = getSupabaseClient();
    if (!client) return { state: "offline" };
    if (!(await AuthService.ensureSignedInForDaily())) return this.classifyErrorResult();
    const playerId = AuthService.getPlayerId();
    if (!playerId) return this.classifyErrorResult();
    try {
      const { data, error } = await client.from("profiles").select("display_name").eq("id", playerId).maybeSingle();
      if (error) return this.classifyErrorResult();
      return { state: "ok", data: data?.display_name ?? null };
    } catch {
      return this.classifyErrorResult();
    }
  }

  async setDisplayName(name: string): Promise<ServiceResult<string>> {
    const validation = validateDisplayName(name);
    if (!validation.valid) return { state: "error", message: validation.error ?? "Invalid name." };
    const auth = await this.ensureSignedIn();
    if (!auth) return this.classifyErrorResult();

    const result = await callFunction<{ displayName: string }>("profile-name", auth.token, { displayName: validation.normalized });
    if (!result.ok) return result.status >= 400 && result.status < 500 ? { state: "error", message: result.message } : this.classifyErrorResult();
    return { state: "ok", data: result.data.displayName };
  }

  /** PR34: attempts are shared across platforms; "your best" is the best on `platform` (the Mobile/Desktop board the caller is on). */
  async getMyStatus(challengeId: string, platform: Platform): Promise<DailyStatusResult> {
    const auth = await this.ensureSignedIn();
    if (!auth) return { state: this.classifyError() };
    const client = getSupabaseClient();
    if (!client) return { state: this.classifyError() };

    try {
      // PR26 follow-up: "Your best" must always reflect THIS client's own
      // current scoring-rules version - see get_my_daily_status()'s own doc.
      const { data, error } = await client.rpc("get_my_daily_status", { p_challenge_id: challengeId, p_rules_version: DAILY_SCORING_RULES_VERSION, p_platform: platform });
      if (error) return { state: this.classifyError() };
      const row = ((data ?? []) as DailyStatusRow[])[0];
      if (!row) return { state: "ok", status: { attemptsReserved: 0, bestCompleted: false, bestScore: null, hasResult: false } };
      return {
        state: "ok",
        status: {
          attemptsReserved: row.attempts_reserved,
          bestCompleted: row.best_completed,
          bestScore: row.best_score,
          hasResult: row.has_result,
        },
      };
    } catch {
      return { state: this.classifyError() };
    }
  }

  /** PR34: only `platform`'s board - legacy `unknown` rows are never mixed in. */
  async getLeaderboard(challengeId: string, platform: Platform, limit = 50, offset = 0): Promise<DailyLeaderboardResult> {
    const client = getSupabaseClient();
    if (!client) return { state: this.classifyError() };

    try {
      const { data, error } = await client.rpc("get_daily_leaderboard", {
        p_challenge_id: challengeId,
        p_limit: limit,
        p_offset: offset,
        // PR26: the leaderboard only ever shows THIS client's current
        // scoring-rules population - see `DAILY_SCORING_RULES_VERSION`'s
        // own doc. A legacy-version best is never silently mixed in.
        p_rules_version: DAILY_SCORING_RULES_VERSION,
        p_platform: platform,
      });
      if (error) return { state: this.classifyError() };
      const rows = (data ?? []) as DailyLeaderboardRpcRow[];
      return {
        state: "ok",
        rows: rows.map((r) => ({
          rank: r.rank,
          displayName: r.display_name,
          completed: r.completed,
          score: r.score,
          activeDurationMs: r.active_duration_ms,
          steps: r.steps,
          dailyPoints: r.daily_points,
        })),
      };
    } catch {
      return { state: this.classifyError() };
    }
  }

  /** PR34: the caller's rank on the same `platform` board `getLeaderboard()` shows. */
  async getMyRank(challengeId: string, platform: Platform): Promise<DailyMyRankResult> {
    const auth = await this.ensureSignedIn();
    if (!auth) return { state: this.classifyError() };
    const client = getSupabaseClient();
    if (!client) return { state: this.classifyError() };

    try {
      const { data, error } = await client.rpc("get_my_daily_rank", { p_challenge_id: challengeId, p_rules_version: DAILY_SCORING_RULES_VERSION, p_platform: platform });
      if (error) return { state: this.classifyError() };
      const row = ((data ?? []) as DailyMyRankRpcRow[])[0];
      if (!row) return { state: "ok", rank: { rank: null, completed: false, score: 0, activeDurationMs: 0, steps: 0, dailyPoints: null, hasResult: false } };
      return {
        state: "ok",
        rank: {
          rank: row.rank,
          completed: row.completed,
          score: row.score,
          activeDurationMs: row.active_duration_ms,
          steps: row.steps,
          dailyPoints: row.daily_points,
          hasResult: row.has_result,
        },
      };
    } catch {
      return { state: this.classifyError() };
    }
  }
}

export const DailyChallengeClient = new DailyChallengeClientImpl();
